// 파일 공유(자료) 페이지 — distributor-desk Design §2.2/§2.3, FR-8/FR-9.
// distributor_assets 라이브러리(유형·국가·총판·제품 스코프) + 버전 업로드(Supabase
// Storage 또는 external_url) + 제공/갱신 이력(무엇·언제·누구·어떤 버전·채널).
//
// 규약: 참조 리스트(countries/partners/products)는 useDistributorData() 에서 읽고,
// 이 페이지가 변경하는 모듈 데이터(assets/versions/provisions)는 자체 state 로 로드해
// create 성공 후 재로드(refresh)한다. 모든 상태 변경은 사용자 클릭(자동 없음).
import { useEffect, useMemo, useState } from "react";
import { isSupabaseConfigured } from "@/lib/supabase";
import { distributorAssetsRepo, distributorProvisionsRepo } from "@/lib/db";
import type {
  NewDistributorAsset,
  NewDistributorAssetVersion,
  NewDistributorProvision,
} from "@/lib/db";
import type {
  DistributorAsset,
  DistributorAssetType,
  DistributorAssetVersion,
  DistributorProvision,
  DistributorProvisionChannel,
} from "@/types";
import { assetObjectPath, signedAssetUrl, uploadAssetFile } from "@/lib/storage";
import {
  Badge,
  Btn,
  Card,
  cx,
  Disclaimer,
  EmptyState,
  Field,
  IconFolder,
  Input,
  PageHeader,
  SectionLabel,
  Select,
  tdCls,
  tdNum,
  thCls,
} from "./ui";
import { useDistributorData } from "./DistributorDeskLayout";

// ── labels ───────────────────────────────────────────────────────────────────
const ASSET_TYPE_LABEL: Record<DistributorAssetType, string> = {
  catalog: "카탈로그",
  certificate: "인증서",
  price_list: "가격표",
  manual: "매뉴얼",
  ifu: "사용설명서",
  marketing: "마케팅",
  other: "기타",
};
const ASSET_TYPES = Object.keys(ASSET_TYPE_LABEL) as DistributorAssetType[];

const CHANNEL_LABEL: Record<DistributorProvisionChannel, string> = {
  email: "이메일",
  portal: "포털",
  meeting: "미팅",
  courier: "특송",
  other: "기타",
};
const CHANNELS = Object.keys(CHANNEL_LABEL) as DistributorProvisionChannel[];

// ── helpers ──────────────────────────────────────────────────────────────────
const today = (): string => new Date().toISOString().slice(0, 10);
const dayOnly = (s: string | null): string => (s ? s.slice(0, 10) : "—");

function errMsg(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

function formatBytes(n: number | null): string {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function urlFileName(url: string): string | null {
  try {
    const u = new URL(url);
    const seg = u.pathname.split("/").filter(Boolean).pop();
    return seg || u.hostname;
  } catch {
    return null;
  }
}

export function MaterialsPage(): JSX.Element {
  const d = useDistributorData();
  const configured = isSupabaseConfigured();

  // module data (self-loaded, refreshed after writes)
  const [assets, setAssets] = useState<DistributorAsset[]>([]);
  const [provisions, setProvisions] = useState<DistributorProvision[]>([]);
  const [loading, setLoading] = useState(true);

  // selection + versions for the selected asset
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const [versions, setVersions] = useState<DistributorAssetVersion[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);

  // filters
  const [filterType, setFilterType] = useState<string>("all");
  const [filterCountry, setFilterCountry] = useState<string>("all");
  const [filterPartner, setFilterPartner] = useState<string>("all");
  const [hideInactive, setHideInactive] = useState(false);

  // new-asset form
  const [newTitle, setNewTitle] = useState("");
  const [newType, setNewType] = useState<DistributorAssetType>("catalog");
  const [newCountry, setNewCountry] = useState("");
  const [newPartner, setNewPartner] = useState("");
  const [newProduct, setNewProduct] = useState("");
  const [createBusy, setCreateBusy] = useState(false);

  // version-upload form
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadUrl, setUploadUrl] = useState("");
  const [uploadNote, setUploadNote] = useState("");
  const [uploadBusy, setUploadBusy] = useState(false);
  const [fileKey, setFileKey] = useState(0); // remount <input type=file> to clear

  // provision form
  const [provPartner, setProvPartner] = useState("");
  const [provCountry, setProvCountry] = useState("");
  const [provVersion, setProvVersion] = useState<number>(1);
  const [provDate, setProvDate] = useState<string>(today());
  const [provChannel, setProvChannel] = useState<DistributorProvisionChannel>("email");
  const [provNote, setProvNote] = useState("");
  const [provAck, setProvAck] = useState(false);
  const [provBusy, setProvBusy] = useState(false);

  const [err, setErr] = useState<string | null>(null);

  // reference lookups (read-only, from layout context)
  const countryLabel = (id: string | null): string => {
    if (!id) return "전 국가 공용";
    const c = d.countries.find((x) => x.id === id);
    return c ? (c.name_ko ?? c.name_en) : id;
  };
  const partnerName = (id: string | null): string => {
    if (!id) return "전 총판 공용";
    return d.partners.find((p) => p.id === id)?.company_name ?? "(미상 총판)";
  };
  const productName = (id: string | null): string => {
    if (!id) return "제품 무관";
    return d.products.find((p) => p.id === id)?.model_name ?? "(미상 제품)";
  };

  // distributor-role partners (fallback to all if none tagged)
  const distributorPartners = useMemo(() => {
    const ds = d.partners.filter((p) => Array.isArray(p.roles) && p.roles.includes("distributor"));
    return ds.length ? ds : d.partners;
  }, [d.partners]);

  // ── initial load ─────────────────────────────────────────────────────────
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!isSupabaseConfigured()) {
        if (alive) setLoading(false);
        return;
      }
      const [a, p] = await Promise.all([
        distributorAssetsRepo.list().catch(() => [] as DistributorAsset[]),
        distributorProvisionsRepo.list().catch(() => [] as DistributorProvision[]),
      ]);
      if (!alive) return;
      setAssets(a);
      setProvisions(p);
      setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, []);

  // ── load versions when an asset is selected ────────────────────────────────
  useEffect(() => {
    let alive = true;
    if (!selectedAssetId) {
      setVersions([]);
      return;
    }
    setVersionsLoading(true);
    (async () => {
      let vs: DistributorAssetVersion[] = [];
      try {
        vs = await distributorAssetsRepo.versions(selectedAssetId);
      } catch {
        vs = [];
      }
      if (!alive) return;
      setVersions(vs);
      setVersionsLoading(false);
      if (vs.length) setProvVersion(Math.max(...vs.map((v) => v.version)));
    })();
    // seed provision-country from the asset scope (form default only)
    const asset = assets.find((a) => a.id === selectedAssetId);
    setProvCountry(asset?.country_id ?? "");
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAssetId]);

  const selectedAsset = useMemo(
    () => assets.find((a) => a.id === selectedAssetId) ?? null,
    [assets, selectedAssetId],
  );

  // ── refreshers ─────────────────────────────────────────────────────────────
  async function refreshAssets(): Promise<void> {
    try {
      setAssets(await distributorAssetsRepo.list());
    } catch {
      /* keep last-known list */
    }
  }
  async function refreshProvisions(): Promise<void> {
    try {
      setProvisions(await distributorProvisionsRepo.list());
    } catch {
      /* keep last-known list */
    }
  }
  async function refreshVersions(): Promise<void> {
    if (!selectedAssetId) return;
    setVersionsLoading(true);
    try {
      setVersions(await distributorAssetsRepo.versions(selectedAssetId));
    } catch {
      /* keep last-known versions */
    } finally {
      setVersionsLoading(false);
    }
  }

  // ── mutations (all user-triggered) ─────────────────────────────────────────
  async function handleCreateAsset(): Promise<void> {
    if (!newTitle.trim()) {
      setErr("자산 제목을 입력하세요.");
      return;
    }
    setCreateBusy(true);
    setErr(null);
    try {
      const row: NewDistributorAsset = {
        title: newTitle.trim(),
        asset_type: newType,
        country_id: newCountry || null,
        partner_id: newPartner || null,
        product_id: newProduct || null,
        current_version: 1,
        is_active: true,
      };
      const created = await distributorAssetsRepo.create(row);
      setNewTitle("");
      setNewCountry("");
      setNewPartner("");
      setNewProduct("");
      await refreshAssets();
      setSelectedAssetId(created.id);
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setCreateBusy(false);
    }
  }

  async function toggleActive(a: DistributorAsset): Promise<void> {
    setErr(null);
    try {
      await distributorAssetsRepo.update(a.id, { is_active: !a.is_active });
      await refreshAssets();
    } catch (e) {
      setErr(errMsg(e));
    }
  }

  async function handleUpload(): Promise<void> {
    if (!selectedAsset) return;
    if (!uploadFile && !uploadUrl.trim()) {
      setErr("파일을 선택하거나 외부 링크(URL)를 입력하세요.");
      return;
    }
    setUploadBusy(true);
    setErr(null);
    try {
      const nextVersion = versions.length
        ? Math.max(...versions.map((v) => v.version)) + 1
        : 1;

      let storage_path: string | null = null;
      let external_url: string | null = null;
      let file_name: string | null = null;
      let mime_type: string | null = null;
      let file_size: number | null = null;

      if (uploadFile) {
        const path = assetObjectPath(
          selectedAsset.id,
          nextVersion,
          uploadFile.name,
          selectedAsset.country_id,
        );
        await uploadAssetFile(path, uploadFile);
        storage_path = path;
        file_name = uploadFile.name;
        mime_type = uploadFile.type || null;
        file_size = uploadFile.size;
      } else {
        const url = uploadUrl.trim();
        external_url = url;
        file_name = urlFileName(url);
      }

      const row: NewDistributorAssetVersion = {
        asset_id: selectedAsset.id,
        version: nextVersion,
        storage_path,
        external_url,
        file_name,
        mime_type,
        file_size,
        checksum: null,
        uploaded_by: null,
        change_note: uploadNote.trim() || null,
      };
      await distributorAssetsRepo.addVersion(row); // also bumps current_version

      setUploadFile(null);
      setUploadUrl("");
      setUploadNote("");
      setFileKey((k) => k + 1);
      await refreshVersions();
      await refreshAssets();
      setProvVersion(nextVersion);
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setUploadBusy(false);
    }
  }

  async function handleDownload(v: DistributorAssetVersion): Promise<void> {
    setErr(null);
    try {
      if (v.storage_path) {
        const url = await signedAssetUrl(v.storage_path);
        window.open(url, "_blank", "noopener,noreferrer");
      } else if (v.external_url) {
        window.open(v.external_url, "_blank", "noopener,noreferrer");
      }
    } catch (e) {
      setErr(errMsg(e));
    }
  }

  async function handleProvision(): Promise<void> {
    if (!selectedAsset) return;
    if (!provPartner) {
      setErr("수신 총판을 선택하세요.");
      return;
    }
    if (!versions.length) {
      setErr("먼저 버전을 업로드한 뒤 제공 이력을 기록하세요.");
      return;
    }
    setProvBusy(true);
    setErr(null);
    try {
      const row: NewDistributorProvision = {
        asset_id: selectedAsset.id,
        asset_version: provVersion,
        partner_id: provPartner,
        country_id: provCountry || null,
        provided_at: provDate,
        provided_by: null,
        channel: provChannel,
        acknowledged: provAck,
        acknowledged_at: provAck ? provDate : null,
        note: provNote.trim(),
      };
      await distributorProvisionsRepo.create(row);
      setProvNote("");
      setProvAck(false);
      await refreshProvisions();
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setProvBusy(false);
    }
  }

  // ── derived views ──────────────────────────────────────────────────────────
  const filteredAssets = useMemo(() => {
    return assets.filter(
      (a) =>
        (filterType === "all" || a.asset_type === filterType) &&
        (filterCountry === "all" || a.country_id === filterCountry) &&
        (filterPartner === "all" || a.partner_id === filterPartner) &&
        (!hideInactive || a.is_active),
    );
  }, [assets, filterType, filterCountry, filterPartner, hideInactive]);

  const assetProvisions = useMemo(() => {
    if (!selectedAsset) return [];
    return provisions
      .filter((p) => p.asset_id === selectedAsset.id)
      .sort((a, b) => (a.provided_at < b.provided_at ? 1 : -1));
  }, [provisions, selectedAsset]);

  const nextVersionLabel = versions.length
    ? Math.max(...versions.map((v) => v.version)) + 1
    : 1;

  // ── render ───────────────────────────────────────────────────────────────
  return (
    <div>
      <PageHeader
        eyebrow="Materials"
        title="파일 공유 · 제공 이력"
        subtitle="자료 배포는 담당자 수동 기록 · 자동 발송 없음 · 버전은 라이브러리형(교체 자유)"
      />

      <div className="space-y-5">
        {!configured && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-[13px] text-amber-800">
            Supabase 미설정 — 자산 열람/업로드가 비활성화됩니다. <code>.env</code> 설정 후 이용하세요.
          </div>
        )}
        {err && (
          <div className="flex items-start justify-between gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-[13px] text-rose-700">
            <span>{err}</span>
            <Btn variant="subtle" onClick={() => setErr(null)}>
              닫기
            </Btn>
          </div>
        )}

        {/* ── 자산 등록 ─────────────────────────────────────────────── */}
        <Card as="section" className="p-4">
          <SectionLabel className="mb-3">새 자산 등록</SectionLabel>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Field label="제목">
              <Input
                value={newTitle}
                placeholder="예: 2026 제품 카탈로그(영문)"
                onChange={(e) => setNewTitle(e.target.value)}
              />
            </Field>
            <Field label="유형">
              <Select
                value={newType}
                onChange={(e) => setNewType(e.target.value as DistributorAssetType)}
              >
                {ASSET_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {ASSET_TYPE_LABEL[t]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="국가 스코프 (선택 · 공란=공용)">
              <Select value={newCountry} onChange={(e) => setNewCountry(e.target.value)}>
                <option value="">전 국가 공용</option>
                {d.countries.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name_ko ?? c.name_en} ({c.id})
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="총판 스코프 (선택 · 공란=공용)">
              <Select value={newPartner} onChange={(e) => setNewPartner(e.target.value)}>
                <option value="">전 총판 공용</option>
                {distributorPartners.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.company_name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="제품 스코프 (선택 · 공란=무관)">
              <Select value={newProduct} onChange={(e) => setNewProduct(e.target.value)}>
                <option value="">제품 무관</option>
                {d.products.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.model_name}
                  </option>
                ))}
              </Select>
            </Field>
            <div className="flex items-end">
              <Btn
                disabled={!configured || createBusy || !newTitle.trim()}
                onClick={handleCreateAsset}
              >
                {createBusy ? "등록 중…" : "자산 등록"}
              </Btn>
            </div>
          </div>
        </Card>

        {/* ── 자산 목록 ─────────────────────────────────────────────── */}
        <Card as="section" className="overflow-hidden">
          <div className="flex flex-wrap items-center gap-2 border-b border-slate-200/70 px-4 py-3">
            <SectionLabel>자산 목록</SectionLabel>
            <span className="text-[11px] text-slate-400">
              총 {assets.length}건 · 표시 {filteredAssets.length}건
            </span>
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <div className="w-32">
                <Select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                  <option value="all">유형: 전체</option>
                  {ASSET_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {ASSET_TYPE_LABEL[t]}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="w-32">
                <Select value={filterCountry} onChange={(e) => setFilterCountry(e.target.value)}>
                  <option value="all">국가: 전체</option>
                  {d.countries.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name_ko ?? c.name_en}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="w-32">
                <Select value={filterPartner} onChange={(e) => setFilterPartner(e.target.value)}>
                  <option value="all">총판: 전체</option>
                  {distributorPartners.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.company_name}
                    </option>
                  ))}
                </Select>
              </div>
              <label className="flex items-center gap-1.5 text-[11px] text-slate-500">
                <input
                  type="checkbox"
                  checked={hideInactive}
                  onChange={(e) => setHideInactive(e.target.checked)}
                />
                비활성 숨기기
              </label>
            </div>
          </div>

          {loading ? (
            <div className="px-4 py-14 text-center text-sm text-slate-400">불러오는 중…</div>
          ) : filteredAssets.length === 0 ? (
            <div className="p-4">
              <EmptyState icon={<IconFolder className="h-8 w-8" />} title="표시할 자산이 없습니다">
                위 “새 자산 등록”으로 카탈로그·인증서·가격표 등을 추가한 뒤 버전을 업로드하세요.
              </EmptyState>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-slate-200/70">
                    {["제목", "유형", "국가", "총판", "제품", "현재버전", "상태", ""].map((h, i) => (
                      <th key={h || i} className={cx(thCls, (i === 5 || i === 7) && "text-right")}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredAssets.map((a) => {
                    const active = a.id === selectedAssetId;
                    return (
                      <tr
                        key={a.id}
                        className={cx(
                          "cursor-pointer transition",
                          active ? "bg-indigo-50" : "hover:bg-slate-50/60",
                        )}
                        onClick={() => setSelectedAssetId(a.id)}
                      >
                        <td className={cx(tdCls, "font-medium text-slate-800")}>{a.title}</td>
                        <td className={tdCls}>
                          <Badge tone="neutral">{ASSET_TYPE_LABEL[a.asset_type]}</Badge>
                        </td>
                        <td className={cx(tdCls, "whitespace-nowrap text-slate-600")}>
                          {countryLabel(a.country_id)}
                        </td>
                        <td className={cx(tdCls, "whitespace-nowrap text-slate-600")}>
                          {partnerName(a.partner_id)}
                        </td>
                        <td className={cx(tdCls, "whitespace-nowrap text-slate-600")}>
                          {productName(a.product_id)}
                        </td>
                        <td className={tdNum}>v{a.current_version}</td>
                        <td className={tdCls}>
                          <Badge tone={a.is_active ? "success" : "neutral"}>
                            {a.is_active ? "활성" : "비활성"}
                          </Badge>
                        </td>
                        <td className={cx(tdCls, "whitespace-nowrap text-right")}>
                          <Btn
                            variant="subtle"
                            disabled={!configured}
                            onClick={(e) => {
                              e.stopPropagation();
                              void toggleActive(a);
                            }}
                          >
                            {a.is_active ? "비활성화" : "활성화"}
                          </Btn>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* ── 선택 자산 상세: 버전 + 제공 이력 ───────────────────────── */}
        {selectedAsset && (
          <section className="space-y-5 rounded-xl border border-indigo-200 bg-indigo-50/30 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <SectionLabel>선택 자산</SectionLabel>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-slate-800">{selectedAsset.title}</span>
                  <Badge tone="neutral">{ASSET_TYPE_LABEL[selectedAsset.asset_type]}</Badge>
                  <span className="text-[11px] text-slate-400">
                    {countryLabel(selectedAsset.country_id)} · {partnerName(selectedAsset.partner_id)}
                  </span>
                </div>
              </div>
              <Btn variant="ghost" onClick={() => setSelectedAssetId(null)}>
                선택 해제
              </Btn>
            </div>

            {/* 버전 관리 */}
            <Card className="p-4">
              <SectionLabel className="mb-3">버전</SectionLabel>
              {versionsLoading ? (
                <p className="text-sm text-slate-400">버전 불러오는 중…</p>
              ) : versions.length === 0 ? (
                <p className="mb-3 rounded-lg border border-dashed border-slate-200 px-3 py-3 text-[13px] text-slate-400">
                  등록된 버전이 없습니다. 아래에서 첫 버전(v1)을 업로드하세요.
                </p>
              ) : (
                <div className="mb-3 overflow-x-auto rounded-lg border border-slate-200">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b border-slate-200/70">
                        {["버전", "파일명", "형식", "크기", "업로드", "변경 메모", ""].map((h, i) => (
                          <th
                            key={h || i}
                            className={cx(thCls, (i === 3 || i === 4 || i === 6) && "text-right")}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {[...versions]
                        .sort((a, b) => b.version - a.version)
                        .map((v) => (
                          <tr key={v.id} className="transition hover:bg-slate-50/60">
                            <td className={tdCls}>
                              <span className="inline-flex items-center gap-1.5">
                                v{v.version}
                                {v.version === selectedAsset.current_version && (
                                  <Badge tone="success">최신</Badge>
                                )}
                              </span>
                            </td>
                            <td className={tdCls}>
                              {v.file_name ?? (v.external_url ? "(외부 링크)" : "—")}
                            </td>
                            <td className={cx(tdCls, "whitespace-nowrap text-slate-500")}>
                              {v.external_url ? "URL" : (v.mime_type ?? "—")}
                            </td>
                            <td className={tdNum}>{formatBytes(v.file_size)}</td>
                            <td className={tdNum}>{dayOnly(v.uploaded_at)}</td>
                            <td className={cx(tdCls, "text-slate-500")}>{v.change_note ?? "—"}</td>
                            <td className={cx(tdCls, "whitespace-nowrap text-right")}>
                              <Btn
                                variant="subtle"
                                disabled={!configured && !v.external_url}
                                onClick={() => void handleDownload(v)}
                              >
                                {v.storage_path ? "다운로드" : "열기"}
                              </Btn>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* 버전 업로드 */}
              <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
                <SectionLabel className="mb-2">
                  새 버전 업로드 (다음 버전: v{nextVersionLabel})
                </SectionLabel>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Field label="파일 선택">
                    <input
                      key={fileKey}
                      type="file"
                      disabled={!configured || uploadBusy}
                      className="w-full text-[13px] text-slate-600 file:mr-2 file:rounded-lg file:border file:border-slate-200 file:bg-white file:px-2.5 file:py-1.5 file:text-xs file:text-slate-600 hover:file:bg-slate-100"
                      onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)}
                    />
                  </Field>
                  <Field label="또는 외부 링크(URL) · 대용량/영상 대안">
                    <Input
                      value={uploadUrl}
                      placeholder="https://…"
                      disabled={uploadBusy || uploadFile != null}
                      onChange={(e) => setUploadUrl(e.target.value)}
                    />
                  </Field>
                  <Field label="변경 메모 (선택)">
                    <Input
                      value={uploadNote}
                      placeholder="예: 가격 개정 반영, 로고 교체"
                      onChange={(e) => setUploadNote(e.target.value)}
                    />
                  </Field>
                  <div className="flex items-end">
                    <Btn
                      disabled={!configured || uploadBusy || (!uploadFile && !uploadUrl.trim())}
                      onClick={handleUpload}
                    >
                      {uploadBusy ? "업로드 중…" : `v${nextVersionLabel} 업로드`}
                    </Btn>
                  </div>
                </div>
                {uploadFile && (
                  <p className="mt-2 text-[11px] text-slate-500">
                    선택: {uploadFile.name} · {formatBytes(uploadFile.size)}
                  </p>
                )}
              </div>
            </Card>

            {/* 제공 이력 */}
            <Card className="p-4">
              <SectionLabel className="mb-3">제공 이력</SectionLabel>

              {/* 기록 폼 */}
              <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50/60 p-3">
                <SectionLabel className="mb-2">
                  제공 기록 (누가·언제·어떤 버전을 받았는지 수동 기록)
                </SectionLabel>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  <Field label="수신 총판">
                    <Select value={provPartner} onChange={(e) => setProvPartner(e.target.value)}>
                      <option value="">— 선택 —</option>
                      {distributorPartners.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.company_name}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field label="국가 (선택)">
                    <Select value={provCountry} onChange={(e) => setProvCountry(e.target.value)}>
                      <option value="">미지정</option>
                      {d.countries.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name_ko ?? c.name_en} ({c.id})
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field label="제공 버전">
                    <Select
                      value={provVersion}
                      disabled={!versions.length}
                      onChange={(e) => setProvVersion(Number(e.target.value))}
                    >
                      {versions.length === 0 && <option value={1}>버전 없음</option>}
                      {[...versions]
                        .sort((a, b) => b.version - a.version)
                        .map((v) => (
                          <option key={v.id} value={v.version}>
                            v{v.version}
                            {v.file_name ? ` · ${v.file_name}` : ""}
                          </option>
                        ))}
                    </Select>
                  </Field>
                  <Field label="제공일">
                    <Input
                      type="date"
                      value={provDate}
                      onChange={(e) => setProvDate(e.target.value)}
                    />
                  </Field>
                  <Field label="채널">
                    <Select
                      value={provChannel}
                      onChange={(e) => setProvChannel(e.target.value as DistributorProvisionChannel)}
                    >
                      {CHANNELS.map((c) => (
                        <option key={c} value={c}>
                          {CHANNEL_LABEL[c]}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field label="메모 (선택)">
                    <Input
                      value={provNote}
                      placeholder="예: 분기 정기 자료 배포"
                      onChange={(e) => setProvNote(e.target.value)}
                    />
                  </Field>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <label className="flex items-center gap-1.5 text-[11px] text-slate-600">
                    <input
                      type="checkbox"
                      checked={provAck}
                      onChange={(e) => setProvAck(e.target.checked)}
                    />
                    수신확인됨으로 표기 (수동)
                  </label>
                  <Btn
                    disabled={!configured || provBusy || !provPartner || !versions.length}
                    onClick={handleProvision}
                  >
                    {provBusy ? "기록 중…" : "제공 기록"}
                  </Btn>
                </div>
              </div>

              {/* 이력 목록 */}
              {assetProvisions.length === 0 ? (
                <p className="rounded-lg border border-dashed border-slate-200 px-3 py-3 text-[13px] text-slate-400">
                  이 자산의 제공 이력이 아직 없습니다.
                </p>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-slate-200">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b border-slate-200/70">
                        {["제공일", "수신 총판", "국가", "버전", "채널", "수신확인", "메모"].map(
                          (h, i) => (
                            <th key={h} className={cx(thCls, i === 3 && "text-right")}>
                              {h}
                            </th>
                          ),
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {assetProvisions.map((p) => (
                        <tr key={p.id} className="transition hover:bg-slate-50/60">
                          <td className={cx(tdCls, "whitespace-nowrap")}>{dayOnly(p.provided_at)}</td>
                          <td className={cx(tdCls, "whitespace-nowrap text-slate-700")}>
                            {partnerName(p.partner_id)}
                          </td>
                          <td className={cx(tdCls, "whitespace-nowrap text-slate-600")}>
                            {p.country_id ? countryLabel(p.country_id) : "—"}
                          </td>
                          <td className={tdNum}>v{p.asset_version}</td>
                          <td className={tdCls}>
                            {p.channel ? <Badge tone="neutral">{CHANNEL_LABEL[p.channel]}</Badge> : "—"}
                          </td>
                          <td className={tdCls}>
                            <Badge tone={p.acknowledged ? "success" : "warn"}>
                              {p.acknowledged ? "확인" : "미확인"}
                            </Badge>
                          </td>
                          <td className={cx(tdCls, "text-slate-500")}>{p.note || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </section>
        )}

        <Disclaimer>
          본 도구는 사전 정의된 항목만 취합·표시합니다. 최종 판단·확인은 담당자 책임입니다. 공유
          파일·제공 이력 등 대외비 자료는 CF Access(SSO) 게이팅 이후 적재하십시오.
        </Disclaimer>
      </div>
    </div>
  );
}
